import { IonButton, IonCol, IonRow } from '@ionic/react'
import { z } from 'zod'
import { ErrorMessage } from '@hookform/error-message'

import { cn } from '../../../utils/cn'
import { nullableString } from '../../../utils/schema'
import { useZodForm } from '../../../utils/use-zod-form'
import { useUpdateClient } from '../../../hooks/clients/mutations'
import { useBoolean } from '../../../hooks/use-boolean'
import { StyledError } from '../../../components/ui/form/errors'
import { Form } from '../../../components/ui/form/form'
import { NumericInput, TextInput } from '../../../components/ui/form/input'
import { InfoCard } from './info-card'

import type { ClientInfo } from '../../../hooks/clients/queries'

type Props = {
  client: ClientInfo
  isEditDisabled: boolean
  updateEditStatus: (status: boolean) => void
}

const contactCardSchema = z.object({
  birthday: z.object({
    day: z
      .number()
      .int({ message: 'Day must be an integer' })
      .min(1, { message: 'Day must be a number between 1 and 31' })
      .max(31, { message: 'Day must be a number beterrn 1 and 31' })
      .nullable(),
    month: z
      .number()
      .int({ message: 'Month must be an integer' })
      .min(1, { message: 'Month must be a number between 1 and 12' })
      .max(12, { message: 'Month must be a number beterrn 1 and 12' })
      .nullable(),
    year: z
      .number()
      .int({ message: 'Year must be an integer' })
      .refine(
        (val) => {
          return val >= 1000 && val <= 9999
        },
        {
          message: 'The Year must be exactly 4 digits.',
        }
      )
      .nullable(),
  }),
  email: z.string().email().or(z.literal('')),
  firstName: z.string(),
  lastName: z.string(),
  street1: nullableString,
  street2: nullableString,
  city: nullableString,
  state: nullableString,
  zip: nullableString,
  country: nullableString,
})

function ContactCard({ client, isEditDisabled, updateEditStatus }: Props) {
  const {
    city,
    country,
    dobObject,
    email,
    firstName,
    lastName,
    state,
    street1,
    street2,
    zip,
  } = client
  const [isEditing, { off: hideEdit, on: showEdit }] = useBoolean()

  const updateClient = useUpdateClient()

  const form = useZodForm({
    defaultValues: {
      birthday: dobObject
        ? Object.keys(dobObject).length
          ? dobObject
          : { day: null, month: null, year: null }
        : { day: null, month: null, year: null },
      email: email ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      street1,
      street2,
      city,
      country,
      state,
      zip,
    },
    schema: contactCardSchema,
  })

  return (
    <InfoCard>
      <Form
        form={form}
        onSubmit={(data) =>
          updateClient.mutate(
            { objectId: client.objectId, ...data },
            {
              onSuccess: () => {
                hideEdit()
                updateEditStatus(true)
              },
            }
          )
        }
      >
        <div>
          <IonRow>
            <IonCol>
              <TextInput
                name="firstName"
                label="First Name"
                color="light"
                disabled={!isEditing}
              />
            </IonCol>
            <IonCol>
              <TextInput
                name="lastName"
                label="Last Name"
                color="light"
                disabled={!isEditing}
              />
            </IonCol>
          </IonRow>
        </div>

        <IonRow>
          <IonCol>
            <TextInput
              name="email"
              label="Email"
              color="light"
              disabled={!isEditing}
            />
            <ErrorMessage
              name="email"
              as={StyledError}
              errors={form.formState.errors}
            />
          </IonCol>
        </IonRow>

        {/* Address */}
        <IonRow>
          <IonCol size="9">
            <TextInput
              name="street1"
              label="Street"
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
          <IonCol size="3">
            <TextInput
              name="street2"
              label="Apt No."
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="5" sizeSm="6">
            <TextInput
              name="city"
              label="City"
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
          <IonCol size="4" sizeSm="2">
            <TextInput
              name="state"
              label="State"
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
          <IonCol size="3" sizeSm="4">
            <TextInput
              name="zip"
              label="Zip Code"
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="12" sizeSm="6">
            <TextInput
              name="country"
              label="Country"
              color="light"
              disabled={!isEditing}
            />
          </IonCol>
        </IonRow>

        <IonRow className="items-end">
          <IonCol size="3">
            <p className={cn('text-xl', { 'text-gray-400': !isEditing })}>
              Birthday
            </p>
          </IonCol>
          <IonCol size="3">
            <NumericInput
              color="light"
              name="birthday.month"
              label="Month"
              disabled={!isEditing}
              fallbackNull
            />
          </IonCol>
          <IonCol size="3">
            <NumericInput
              color="light"
              name="birthday.day"
              label="Day"
              disabled={!isEditing}
              fallbackNull
            />
          </IonCol>
          <IonCol size="3">
            <NumericInput
              color="light"
              name="birthday.year"
              label="Year"
              disabled={!isEditing}
              fallbackNull
            />
          </IonCol>
          {form.formState.errors.birthday ? (
            <IonCol size="9">
              <ErrorMessage
                as={StyledError}
                errors={form.formState.errors}
                name="birthday.month"
              />
              <ErrorMessage
                as={StyledError}
                errors={form.formState.errors}
                name="birthday.day"
              />
              <ErrorMessage
                as={StyledError}
                errors={form.formState.errors}
                name="birthday.year"
              />
            </IonCol>
          ) : null}
        </IonRow>

        <div className={isEditing ? 'flex justify-between' : 'hidden'}>
          <IonButton
            fill="outline"
            size="small"
            onClick={() => {
              hideEdit()
              updateEditStatus(true)
            }}
          >
            Cancel
          </IonButton>
          <IonButton
            color="yellow"
            size="small"
            type="submit"
            // onClick={handleSubmit(handleSave)}
            // disabled={isSaveDisabled}
          >
            Save
          </IonButton>
        </div>

        <div className={isEditing ? 'hidden' : 'flex justify-end'}>
          <IonButton
            size="small"
            fill="outline"
            disabled={isEditDisabled}
            onClick={() => {
              showEdit()
              updateEditStatus(false)
            }}
          >
            Edit
          </IonButton>
        </div>
      </Form>
    </InfoCard>
  )
}

export { ContactCard }
